import React, { useMemo, useEffect, useState } from "react";
import {
  AdminCandidateDetail,
  CandidateRow,
  AdminCandidateListRow,
} from "../admin-types";
import { DetailRow, Empty, Panel, Section } from "../AdminUI";
import { adminService } from "../admin.service";

interface Props {
  candidateId: string;
  cachedRows: Array<CandidateRow | AdminCandidateListRow> | undefined;
  cached: AdminCandidateDetail | undefined;
  onCache: (id: string, data: AdminCandidateDetail) => void;
  refreshKey: number;
}

const formatDate = (dateString: string | undefined) => {
  if (!dateString || dateString.startsWith("1900")) return "Presente"; // Handle '1900' as current/present if logic applies
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
  });
};

export default function CandidateView({
  candidateId,
  cachedRows,
  cached,
  onCache,
  refreshKey,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fetchedData, setFetchedData] = useState<AdminCandidateDetail | null>(
    null
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const candidateRow = useMemo(() => {
    if (!cachedRows || !candidateId) return null;
    return cachedRows.find(
      (r) =>
        r.candidate_id === candidateId ||
        r.user_id === candidateId ||
        ("id" in r && r.id === candidateId)
    );
  }, [cachedRows, candidateId]);

  useEffect(() => {
    let mounted = true;

    const loadFullDetails = async () => {
      if (cached && refreshKey === 0) {
        setFetchedData(cached);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        const lookupId =
          candidateRow?.user_id ||
          (candidateRow && "user" in candidateRow ? candidateRow.user : undefined) ||
          candidateRow?.candidate_id ||
          (candidateRow && "id" in candidateRow ? candidateRow.id : undefined) ||
          candidateId;
        console.log("[Admin] candidate detail lookup", {
          candidateId,
          user_id: candidateRow?.user_id,
          candidate_id: candidateRow?.candidate_id,
          row_id: candidateRow && "id" in candidateRow ? candidateRow.id : undefined,
          row_user: candidateRow && "user" in candidateRow ? candidateRow.user : undefined,
          lookupId,
        });
        const detailedData = await adminService.fetchCandidate(lookupId);

        if (mounted && detailedData) {
          setFetchedData(detailedData);
          onCache(candidateId, detailedData);
        }
      } catch (error: any) {
        if (mounted) {
          setLoadError(error?.message || "No se pudo cargar el candidato.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadFullDetails();

    return () => {
      mounted = false;
    };
  }, [candidateId, candidateRow, refreshKey]);

  if (!candidateId) return null;

  const displayData =
    fetchedData || (candidateRow as unknown as AdminCandidateDetail);
  const isMissing = !fetchedData && !loading && !!candidateRow;
  const candidateIdDisplay =
    displayData?.id ||
    candidateRow?.candidate_id ||
    (candidateRow && "id" in candidateRow ? candidateRow.id : candidateId);
  const seenValue =
    candidateRow && "is_seen" in candidateRow ? candidateRow.is_seen : undefined;
  const statusValue =
    candidateRow && "status" in candidateRow ? candidateRow.status : undefined;
  const createdValue =
    candidateRow && "created_at" in candidateRow
      ? candidateRow.created_at
      : displayData?.created_at;

  // --- Data Preparation Helpers ---
  const formattedSkills =
    displayData?.skill?.map((s: any) => s.description || s.name) || [];
  const formattedLanguages =
    displayData?.language?.map(
      (l: any) =>
        `${l.language_en || l.language_es || "Unknown"} (Nivel ${l.level})`
    ) || [];

  const rowWithId =
    candidateRow && "id" in candidateRow
      ? (candidateRow as AdminCandidateListRow)
      : null;
  const passwordTargetId =
    displayData?.id ||
    candidateRow?.user_id ||
    rowWithId?.user ||
    candidateRow?.candidate_id ||
    rowWithId?.id ||
    candidateId;

  const handleSetPassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setPasswordError("Debes completar ambos campos de contraseña.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Las contraseñas no coinciden.");
      return;
    }

    try {
      setPasswordSaving(true);
      await adminService.setCandidatePassword(passwordTargetId, newPassword);
      setPasswordSuccess("Contraseña actualizada correctamente.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "No se pudo actualizar la contraseña.";
      setPasswordError(String(message));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Panel title="Detalles del candidato">
      {!displayData && loading ? (
        <Empty>Cargando candidato…</Empty>
      ) : !displayData ? (
        <Empty>{loadError || "Candidato no encontrado."}</Empty>
      ) : (
        <>
          {/* --- HEADER --- */}
          <div
            className="company-details"
            style={{
              marginBottom: "24px",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "16px",
            }}
          >
            <div className="company-header">
              <div className="company-logo-placeholder">
                {(displayData.first_name || "").charAt(0)}
                {(displayData.last_name || "").charAt(0)}
              </div>

              <div style={{ flex: 1, marginLeft: "12px" }}>
                <div className="flex items-center gap-2">
                  <h2
                    className="company-name"
                    style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}
                  >
                    {displayData.first_name} {displayData.last_name}
                  </h2>
                  {loading && (
                    <span className="text-xs text-blue-500 animate-pulse ml-2">
                      (Cargando...)
                    </span>
                  )}
                </div>
                <p
                  className="company-desc"
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginTop: "2px",
                  }}
                >
                  {displayData.city ? `${displayData.city}, ` : ""}
                  {displayData.country}
                </p>

                {isMissing && (
                  <p className="text-xs text-orange-500 mt-1">
                    ⚠️ Datos detallados no encontrados. Mostrando información
                    básica.
                  </p>
                )}
              </div>

              {/* Download CV Button */}
              {displayData.cv && displayData.cv.length > 0 && (
                <a
                  href={displayData.cv[0].file}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition"
                  style={{ textDecoration: "none" }}
                >
                  Descargar CV
                </a>
              )}
            </div>
          </div>

          {/* --- DETAILS GRID --- */}
          <div
            className="details-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* LEFT COLUMN */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
            >
              <Section title="Información Personal">
                <DetailRow label="Email" value={displayData.email} />
                <DetailRow label="Teléfono" value={displayData.phone} />
                <DetailRow label="NIF/DNI" value={displayData.nif} />
                <DetailRow
                  label="Fecha Nac."
                  value={formatDate(displayData.dob)}
                />
                <DetailRow label="Dirección" value={displayData.address} />
                <DetailRow label="Ciudad" value={displayData.city} />
                <DetailRow label="País" value={displayData.country} />
              </Section>

              <Section title="Estatus y Sistema">
                <DetailRow label="ID Candidato" value={candidateIdDisplay} />
                <DetailRow label="Estado" value={statusValue || "—"} />
                <DetailRow
                  label="Visto"
                  value={seenValue ?? "—"}
                  isBool={typeof seenValue === "boolean"}
                />
                <DetailRow
                  label="Vehículo"
                  value={displayData.is_vehicle}
                  isBool
                />
                <DetailRow
                  label="Creado"
                  value={
                    createdValue
                      ? new Date(createdValue).toLocaleDateString()
                      : "—"
                  }
                />
              </Section>

              <Section title="Seguridad">
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <input
                    type="password"
                    className="input"
                    placeholder="Nueva contraseña"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <input
                    type="password"
                    className="input"
                    placeholder="Confirmar contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <div>
                    <button
                      className="action-btn solid"
                      onClick={handleSetPassword}
                      disabled={passwordSaving}
                    >
                      {passwordSaving ? "Actualizando..." : "Actualizar contraseña"}
                    </button>
                  </div>
                  {passwordError && (
                    <span style={{ color: "#b91c1c", fontSize: "12px" }}>
                      {passwordError}
                    </span>
                  )}
                  {passwordSuccess && (
                    <span style={{ color: "#166534", fontSize: "12px" }}>
                      {passwordSuccess}
                    </span>
                  )}
                </div>
              </Section>

              <Section title="Skills & Idiomas">
                {/* SKILLS */}
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                  >
                    Skills
                  </div>
                  <div className="tags-wrapper">
                    {formattedSkills.length > 0 ? (
                      formattedSkills.map((skill: string, i: number) => (
                        <span key={i} className="tag blue">
                          {skill}
                        </span>
                      ))
                    ) : (
                      <span className="tag-empty">Sin skills registradas</span>
                    )}
                  </div>
                </div>

                {/*LANGUAGES */}
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                  >
                    Idiomas
                  </div>
                  <div className="tags-wrapper">
                    {formattedLanguages.length > 0 ? (
                      formattedLanguages.map((lang: string, i: number) => (
                        <span key={i} className="tag gray">
                          {lang}
                        </span>
                      ))
                    ) : (
                      <span className="tag-empty">Sin idiomas registrados</span>
                    )}
                  </div>
                </div>
              </Section>
            </div>

            {/* RIGHT COLUMN */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
            >
              {/* EXPERIENCE SECTION */}
              <Section title="Experiencia Profesional">
                {!displayData.experience ||
                displayData.experience.length === 0 ? (
                  <div className="text-gray-400 text-sm italic">
                    Sin experiencia registrada
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {displayData.experience.map((exp: any) => (
                      <div
                        key={exp.id}
                        style={{
                          borderLeft: "3px solid #e5e7eb",
                          paddingLeft: "12px",
                          marginBottom: "16px",
                        }}
                      >
                        <div className="font-bold text-sm text-gray-800">
                          {exp.job_position}
                        </div>
                        <div className="text-sm text-blue-600 font-medium">
                          {exp.company}
                        </div>
                        <div className="text-xs text-gray-500 mb-1">
                          {formatDate(exp.start_date)} -{" "}
                          {formatDate(exp.end_date)}
                        </div>
                        {exp.location && (
                          <div className="text-xs text-gray-400 mb-2">
                            📍 {exp.location}
                          </div>
                        )}
                        {exp.description && (
                          <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                            {exp.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* EDUCATION SECTION */}
              <Section title="Educación">
                {!displayData.education ||
                displayData.education.length === 0 ? (
                  <div className="text-gray-400 text-sm italic">
                    Sin educación registrada
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {displayData.education.map((edu: any) => (
                      <div key={edu.id} style={{ marginBottom: "12px" }}>
                        <div className="font-bold text-sm text-gray-800">
                          {edu.title || edu.level}
                        </div>
                        <div className="text-sm text-gray-600">
                          {edu.institution}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(edu.start_date)} -{" "}
                          {formatDate(edu.end_date)}
                        </div>
                        {edu.description && (
                          <p className="text-xs text-gray-500 mt-1">
                            {edu.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* NOTES SECTION */}
              {displayData.notes && displayData.notes.length > 0 && (
                <Section title="Notas Internas">
                  {displayData.notes.map((note: any) => (
                    <div
                      key={note.id}
                      className="bg-yellow-50 p-2 rounded mb-2 border border-yellow-100"
                    >
                      <p className="text-xs text-gray-800">{note.note}</p>
                      <span className="text-[10px] text-gray-400 block mt-1">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </Section>
              )}
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}
